import { useState, useMemo } from 'react';
import DOMPurify from 'dompurify';
import { useParams, Link } from 'react-router';
import {
    ArrowLeft,
    Clock,
    Users,
    ChefHat,
    Minus,
    Plus,
    Printer,
    Star,
    Heart,
    Share2,
    CheckCircle2,
    UtensilsCrossed,
    Loader2,
    ShoppingCart,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import toast from 'react-hot-toast';
import { useRecipe, useSaveRecipe, useRateRecipe, type Recipe } from '@/services/recipes';
import AddToShoppingListDialog from '@/components/AddToShoppingListDialog';
import { formatAmount } from '@/lib/utils';
import { RecipeImage } from '@/components/RecipeImage';

const RecipeDetail = () => {
    const { id } = useParams<{ id: string }>();
    const { data: recipe, isLoading, error } = useRecipe(id || '');

    if (isLoading) {
        return (
            <div className="min-h-screen bg-linear-to-b from-emerald-50/50 to-white">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    <div className="animate-pulse">
                        <div className="h-8 w-32 bg-gray-200 rounded mb-6" />
                        <div className="aspect-video bg-gray-200 rounded-3xl mb-8" />
                        <div className="h-10 w-3/4 bg-gray-200 rounded mb-4" />
                        <div className="h-6 w-1/2 bg-gray-200 rounded" />
                    </div>
                </div>
            </div>
        );
    }

    if (error || !recipe) {
        return (
            <div className="min-h-screen bg-linear-to-b from-emerald-50/50 to-white">
                <div className="max-w-4xl mx-auto px-6 py-8">
                    <div className="text-center py-16 bg-white rounded-3xl shadow-sm">
                        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <ChefHat className="w-10 h-10 text-emerald-600" />
                        </div>
                        <h3 className="text-xl font-semibold text-gray-700 mb-2">
                            Recipe not found
                        </h3>
                        <p className="text-gray-500 mb-6">
                            This recipe may have been removed or is not available.
                        </p>
                        <Link to="/recipes">
                            <Button className="bg-emerald-600 hover:bg-emerald-700">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back to Recipes
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Keyed by recipe so every piece of per-recipe state (servings, ticked
    // ingredients, the rating form) starts fresh when navigating to another recipe.
    return <RecipeDetailBody key={recipe.id} recipe={recipe} />;
};

const RecipeDetailBody = ({ recipe }: { recipe: Recipe }) => {
    const saveRecipeMutation = useSaveRecipe();
    const rateRecipeMutation = useRateRecipe();
    // How many servings the client is preparing. Ingredient amounts are stored
    // for the whole recipe (recipe.servings), so they scale by servings / recipe.servings.
    // Starts at the recipe's own yield so amounts and the shopping list are unscaled.
    const [servings, setServings] = useState(() => Math.max(1, recipe.servings || 1));
    const [showAddToList, setShowAddToList] = useState(false);
    const [checkedIngredients, setCheckedIngredients] = useState<Set<string>>(new Set());
    const [hoverRating, setHoverRating] = useState(0);
    const [selectedRating, setSelectedRating] = useState(0);
    const [reviewText, setReviewText] = useState('');
    const [showRatingForm, setShowRatingForm] = useState(false);

    // Recipe is saved if saves array has items
    const isSaved = recipe.saves && recipe.saves.length > 0;

    // Get user's existing rating if any
    const userRating = recipe.ratings?.[0];

    const handleSaveRecipe = async () => {
        try {
            const result = await saveRecipeMutation.mutateAsync(recipe.id);
            toast.success(result.saved ? 'Recipe saved!' : 'Recipe removed from saved');
        } catch {
            toast.error('Failed to update saved status');
        }
    };

    const handleSubmitRating = async () => {
        if (selectedRating === 0) return;
        try {
            await rateRecipeMutation.mutateAsync({
                recipeId: recipe.id,
                data: { rating: selectedRating, review: reviewText || undefined },
            });
            toast.success('Rating submitted!');
            setShowRatingForm(false);
            setReviewText('');
        } catch {
            toast.error('Failed to submit rating');
        }
    };

    // Sanitize HTML content to prevent XSS attacks
    const sanitizedInstructions = useMemo(() => {
        if (!recipe.instructions) return '';
        return DOMPurify.sanitize(recipe.instructions, {
            ALLOWED_TAGS: [
                'p',
                'br',
                'strong',
                'em',
                'ul',
                'ol',
                'li',
                'h1',
                'h2',
                'h3',
                'h4',
                'h5',
                'h6',
                'a',
                'span',
                'div',
            ],
            ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
        });
    }, [recipe.instructions]);

    const toggleIngredient = (id: string) => {
        setCheckedIngredients((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const totalTime = (recipe.prepTime || 0) + (recipe.cookTime || 0);

    // Meal types (Breakfasts, Lunches, ...) replaced cuisine as the recipe's label.
    const mealTypeLabel = recipe.categories?.map((c) => c.name).join(', ') || null;

    const recipeServings = recipe.servings || 1;
    const adjustQuantity = (quantity: number) =>
        formatAmount((quantity * servings) / recipeServings);

    const handlePrint = () => {
        window.print();
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: recipe.name,
                    text: recipe.description || `Check out this recipe: ${recipe.name}`,
                    url: window.location.href,
                });
            } catch {
                // User cancelled or error
            }
        }
    };

    return (
        <div className="min-h-screen bg-linear-to-b from-emerald-50/50 to-white print:bg-white">
            {/* Hero Image */}
            {recipe.imageUrl && (
                <div className="relative h-64 md:h-80 lg:h-96 print:hidden">
                    <RecipeImage
                        src={recipe.imageUrl}
                        alt={recipe.name}
                        variant="hero"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-black/60 via-black/20 to-transparent" />

                    {/* Back Button */}
                    <Link
                        to="/recipes"
                        className="absolute top-6 left-6 flex items-center gap-2 text-white bg-black/30 hover:bg-black/50 px-4 py-2 rounded-full backdrop-blur-sm transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        <span className="text-sm font-medium">Back</span>
                    </Link>

                    {/* Action Buttons */}
                    <div className="absolute top-6 right-6 flex gap-2 print:hidden">
                        <button
                            onClick={handleShare}
                            className="w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-gray-700 shadow-lg transition-colors"
                        >
                            <Share2 className="w-5 h-5" />
                        </button>
                        <button
                            onClick={handleSaveRecipe}
                            disabled={saveRecipeMutation.isPending}
                            className={`w-10 h-10 rounded-full flex items-center justify-center shadow-lg transition-colors ${
                                isSaved
                                    ? 'bg-rose-500 hover:bg-rose-600 text-white'
                                    : 'bg-white/90 hover:bg-white text-gray-700'
                            }`}
                        >
                            {saveRecipeMutation.isPending ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                            ) : (
                                <Heart className={`w-5 h-5 ${isSaved ? 'fill-current' : ''}`} />
                            )}
                        </button>
                        <button
                            onClick={handlePrint}
                            className="w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center text-gray-700 shadow-lg transition-colors"
                        >
                            <Printer className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}

            <div className="max-w-4xl mx-auto px-6 py-8">
                {/* Back link if no image */}
                {!recipe.imageUrl && (
                    <Link
                        to="/recipes"
                        className="inline-flex items-center text-gray-600 hover:text-emerald-600 mb-6 transition-colors print:hidden"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Recipes
                    </Link>
                )}

                {/* Recipe Header */}
                <div
                    className={`bg-white rounded-3xl shadow-sm p-6 md:p-8 ${recipe.imageUrl ? '-mt-16 relative z-10' : ''}`}
                >
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                                {recipe.name}
                            </h1>
                            {recipe.description && (
                                <p className="text-gray-600 text-lg">{recipe.description}</p>
                            )}
                        </div>

                        {/* Rating */}
                        {!!recipe.averageRating && recipe.averageRating > 0 && (
                            <div className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-xl shrink-0">
                                <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                                <span className="text-lg font-bold text-amber-700">
                                    {recipe.averageRating.toFixed(1)}
                                </span>
                                {recipe.totalRatings && (
                                    <span className="text-sm text-amber-600">
                                        ({recipe.totalRatings})
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                        {mealTypeLabel && (
                            <div className="bg-purple-50 rounded-xl p-4 text-center">
                                <UtensilsCrossed className="w-5 h-5 text-purple-600 mx-auto mb-2" />
                                <div className="text-sm text-gray-500">Meal Type</div>
                                <div className="font-semibold text-gray-900">{mealTypeLabel}</div>
                            </div>
                        )}
                        {!!recipe.prepTime && (
                            <div className="bg-emerald-50 rounded-xl p-4 text-center">
                                <Clock className="w-5 h-5 text-emerald-600 mx-auto mb-2" />
                                <div className="text-sm text-gray-500">Prep Time</div>
                                <div className="font-semibold text-gray-900">
                                    {recipe.prepTime} min
                                </div>
                            </div>
                        )}
                        {!!recipe.cookTime && (
                            <div className="bg-emerald-50 rounded-xl p-4 text-center">
                                <Clock className="w-5 h-5 text-emerald-600 mx-auto mb-2" />
                                <div className="text-sm text-gray-500">Cook Time</div>
                                <div className="font-semibold text-gray-900">
                                    {recipe.cookTime} min
                                </div>
                            </div>
                        )}
                        {totalTime > 0 && (
                            <div className="bg-teal-50 rounded-xl p-4 text-center">
                                <Clock className="w-5 h-5 text-teal-600 mx-auto mb-2" />
                                <div className="text-sm text-gray-500">Total Time</div>
                                <div className="font-semibold text-gray-900">{totalTime} min</div>
                            </div>
                        )}
                        {recipe.difficulty && (
                            <div className="bg-gray-50 rounded-xl p-4 text-center">
                                <ChefHat className="w-5 h-5 text-gray-600 mx-auto mb-2" />
                                <div className="text-sm text-gray-500">Difficulty</div>
                                <div className="font-semibold text-gray-900 capitalize">
                                    {recipe.difficulty.toLowerCase()}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Macros Info (protein, carbs, fat - no calories) */}
                    {!!(recipe.protein || recipe.carbs || recipe.fat) && (
                        <div className="bg-linear-to-r from-emerald-50 to-teal-50 rounded-xl p-4 mb-6">
                            <h3 className="text-sm font-medium text-gray-500 mb-3">
                                Macros per serving
                            </h3>
                            <div className="grid grid-cols-3 gap-4 text-center">
                                {!!recipe.protein && (
                                    <div>
                                        <div className="text-2xl font-bold text-emerald-600">
                                            {Math.round(recipe.protein)}g
                                        </div>
                                        <div className="text-xs text-gray-500">Protein</div>
                                    </div>
                                )}
                                {!!recipe.carbs && (
                                    <div>
                                        <div className="text-2xl font-bold text-blue-600">
                                            {Math.round(recipe.carbs)}g
                                        </div>
                                        <div className="text-xs text-gray-500">Carbs</div>
                                    </div>
                                )}
                                {!!recipe.fat && (
                                    <div>
                                        <div className="text-2xl font-bold text-amber-600">
                                            {Math.round(recipe.fat)}g
                                        </div>
                                        <div className="text-xs text-gray-500">Fat</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Categories & Tags */}
                    {((recipe.dietaryTags && recipe.dietaryTags.length > 0) ||
                        (recipe.nutritionCategories && recipe.nutritionCategories.length > 0)) && (
                        <div className="flex flex-wrap gap-2">
                            {recipe.dietaryTags?.map((tag) => (
                                <span
                                    key={tag.id}
                                    className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700"
                                >
                                    {tag.name}
                                </span>
                            ))}
                            {recipe.nutritionCategories?.map((nc) => {
                                const totalPoints = nc.servings * nc.category.points;
                                const isPositive = totalPoints >= 0;
                                return (
                                    <span
                                        key={nc.id}
                                        className={`px-3 py-1 rounded-full text-sm font-medium ${
                                            isPositive
                                                ? 'bg-green-100 text-green-700'
                                                : 'bg-orange-100 text-orange-700'
                                        }`}
                                    >
                                        {nc.category.name}
                                        {nc.servings > 1 && (
                                            <span className="opacity-75"> ×{nc.servings}</span>
                                        )}
                                        {totalPoints !== 0 && (
                                            <span className="ml-1 opacity-75">
                                                ({totalPoints > 0 ? '+' : ''}
                                                {totalPoints})
                                            </span>
                                        )}
                                    </span>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Main Content Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-8">
                    {/* Ingredients */}
                    <div className="lg:col-span-1">
                        <div className="bg-white rounded-2xl p-6 shadow-sm sticky top-24">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Ingredients</h2>

                            {/* Servings Adjuster */}
                            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl mb-4 print:hidden">
                                <div className="flex items-center gap-2">
                                    <Users className="w-4 h-4 text-emerald-600 shrink-0" />
                                    <span className="text-sm font-medium text-emerald-900">
                                        Servings
                                    </span>
                                </div>
                                <div className="flex items-center">
                                    <button
                                        onClick={() => setServings((s) => Math.max(1, s - 1))}
                                        aria-label="Fewer servings"
                                        className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-emerald-600 hover:bg-emerald-100 transition-colors shadow-sm shrink-0"
                                    >
                                        <Minus className="w-3 h-3" />
                                    </button>
                                    <span className="w-8 text-center font-bold text-emerald-900 text-sm">
                                        {servings}
                                    </span>
                                    <button
                                        onClick={() => setServings((s) => s + 1)}
                                        aria-label="More servings"
                                        className="w-7 h-7 rounded-lg bg-white flex items-center justify-center text-emerald-600 hover:bg-emerald-100 transition-colors shadow-sm shrink-0"
                                    >
                                        <Plus className="w-3 h-3" />
                                    </button>
                                </div>
                            </div>

                            {/* Ingredients List */}
                            {recipe.ingredients && recipe.ingredients.length > 0 ? (
                                <ul className="space-y-2">
                                    {recipe.ingredients.map((ing) => (
                                        <li
                                            key={ing.id}
                                            onClick={() => toggleIngredient(ing.id)}
                                            className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                                                checkedIngredients.has(ing.id)
                                                    ? 'bg-emerald-50 text-gray-400 line-through'
                                                    : 'hover:bg-gray-50'
                                            }`}
                                        >
                                            <div
                                                className={`w-5 h-5 rounded-full border-2 shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                                                    checkedIngredients.has(ing.id)
                                                        ? 'bg-emerald-500 border-emerald-500'
                                                        : 'border-gray-300'
                                                }`}
                                            >
                                                {checkedIngredients.has(ing.id) && (
                                                    <CheckCircle2 className="w-4 h-4 text-white" />
                                                )}
                                            </div>
                                            <div>
                                                <span className="font-medium">
                                                    {adjustQuantity(ing.amount)} {ing.unit}
                                                </span>{' '}
                                                <span className="text-gray-700">
                                                    {ing.ingredient.name}
                                                </span>
                                                {ing.preparation && (
                                                    <span className="text-gray-500 text-sm block">
                                                        ({ing.preparation})
                                                    </span>
                                                )}
                                                {ing.isOptional && (
                                                    <span className="text-gray-400 text-xs ml-1">
                                                        (optional)
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-gray-500 text-sm">No ingredients listed</p>
                            )}

                            {recipe.ingredients && recipe.ingredients.length > 0 && (
                                <Button
                                    onClick={() => setShowAddToList(true)}
                                    className="mt-4 w-full bg-emerald-600 text-white hover:bg-emerald-700 print:hidden"
                                >
                                    <ShoppingCart className="mr-2 h-4 w-4" />
                                    Add to Shopping List
                                </Button>
                            )}
                        </div>
                    </div>

                    {/* Instructions */}
                    <div className="lg:col-span-2">
                        <div className="bg-white rounded-2xl p-6 shadow-sm">
                            <h2 className="text-xl font-bold text-gray-900 mb-6">Instructions</h2>

                            {recipe.instructions ? (
                                <div
                                    className="prose prose-emerald max-w-none text-gray-700 leading-relaxed"
                                    dangerouslySetInnerHTML={{ __html: sanitizedInstructions }}
                                />
                            ) : (
                                <p className="text-gray-500">No instructions provided</p>
                            )}
                        </div>

                        {/* Tips section placeholder */}
                        <div className="bg-linear-to-r from-emerald-500 to-teal-500 rounded-2xl p-6 mt-6 text-white print:hidden">
                            <h3 className="font-bold text-lg mb-2">Chef's Tips</h3>
                            <p className="text-emerald-100 text-sm">
                                For best results, read through the entire recipe before starting.
                                Prepare all your ingredients (mise en place) to make cooking
                                smoother and more enjoyable.
                            </p>
                        </div>

                        {/* Rating Section */}
                        <div className="bg-white rounded-2xl p-6 mt-6 shadow-sm print:hidden">
                            <h3 className="font-bold text-lg mb-4">Rate this Recipe</h3>

                            {userRating ? (
                                <div className="flex flex-col gap-3">
                                    <div className="flex items-center gap-2">
                                        <span className="text-gray-600">You rated:</span>
                                        <div className="flex gap-1">
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Star
                                                    key={star}
                                                    className={`w-5 h-5 ${
                                                        star <= userRating.rating
                                                            ? 'text-amber-400 fill-amber-400'
                                                            : 'text-gray-300'
                                                    }`}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                    {userRating.review && (
                                        <p className="text-gray-600 text-sm italic">
                                            "{userRating.review}"
                                        </p>
                                    )}
                                    <button
                                        onClick={() => {
                                            setSelectedRating(userRating.rating);
                                            setReviewText(userRating.review || '');
                                            setShowRatingForm(true);
                                        }}
                                        className="text-sm text-emerald-600 hover:text-emerald-700 self-start"
                                    >
                                        Update your rating
                                    </button>
                                </div>
                            ) : !showRatingForm ? (
                                <button
                                    onClick={() => setShowRatingForm(true)}
                                    className="flex items-center gap-2 text-gray-600 hover:text-amber-500 transition-colors"
                                >
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <Star key={star} className="w-6 h-6 text-gray-300" />
                                        ))}
                                    </div>
                                    <span className="text-sm">Click to rate</span>
                                </button>
                            ) : (
                                <div className="space-y-4">
                                    {/* Star Rating Input */}
                                    <div className="flex gap-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                onMouseEnter={() => setHoverRating(star)}
                                                onMouseLeave={() => setHoverRating(0)}
                                                onClick={() => setSelectedRating(star)}
                                                className="focus:outline-none"
                                            >
                                                <Star
                                                    className={`w-8 h-8 transition-colors ${
                                                        star <= (hoverRating || selectedRating)
                                                            ? 'text-amber-400 fill-amber-400'
                                                            : 'text-gray-300 hover:text-amber-200'
                                                    }`}
                                                />
                                            </button>
                                        ))}
                                    </div>

                                    {/* Review Text */}
                                    <textarea
                                        value={reviewText}
                                        onChange={(e) => setReviewText(e.target.value)}
                                        placeholder="Add a review (optional)"
                                        className="w-full p-3 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                                        rows={3}
                                    />

                                    {/* Action Buttons */}
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={handleSubmitRating}
                                            disabled={
                                                selectedRating === 0 || rateRecipeMutation.isPending
                                            }
                                            className="bg-emerald-600 hover:bg-emerald-700"
                                        >
                                            {rateRecipeMutation.isPending ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                    Submitting...
                                                </>
                                            ) : (
                                                'Submit Rating'
                                            )}
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setShowRatingForm(false);
                                                setSelectedRating(0);
                                                setReviewText('');
                                                setHoverRating(0);
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <AddToShoppingListDialog
                open={showAddToList}
                onClose={() => setShowAddToList(false)}
                recipes={[
                    {
                        recipeId: recipe.id,
                        servings,
                    },
                ]}
                defaultName={recipe.name}
            />
        </div>
    );
};

export default RecipeDetail;
